import axios from "axios";

const baseUrl = process.env.REACT_APP_DIFLEX_API_URL + "/agreements";

const jsonHeaders = (apiAccessToken) => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${apiAccessToken}`,
});

const failWith = (error, fallback) =>
  new Error(
    (typeof error?.response?.data === "string" && error.response.data) ||
      error?.response?.data?.message ||
      fallback
  );

/**
 * Hardware catalogue administration (feature #301). All catalogue and assortment writes are
 * Admin-only server side; the API rejects a non-Admin token regardless of what the UI shows.
 */

export async function getHardwareProducts(apiAccessToken, includeUnpublished = true) {
  try {
    const response = await axios.get(`${baseUrl}/HardwareProducts`, {
      params: { includeUnpublished },
      headers: jsonHeaders(apiAccessToken),
    });
    return response.data;
  } catch (error) {
    throw failWith(error, "Failed to fetch hardware products");
  }
}

export async function getHardwareProduct(hardwareProductPublicId, apiAccessToken) {
  try {
    const response = await axios.get(
      `${baseUrl}/HardwareProducts/${encodeURIComponent(hardwareProductPublicId)}`,
      { headers: jsonHeaders(apiAccessToken) }
    );
    return response.data;
  } catch (error) {
    throw failWith(error, "Failed to fetch hardware product");
  }
}

export async function createHardwareProduct(productData, apiAccessToken) {
  try {
    const response = await axios.post(`${baseUrl}/HardwareProducts`, productData, {
      headers: jsonHeaders(apiAccessToken),
    });
    return response.data;
  } catch (error) {
    throw failWith(error, "Failed to create hardware product");
  }
}

/** Full replacement; the server rejects the save with 409 when the product changed since it was loaded. */
export async function updateHardwareProduct(productData, apiAccessToken) {
  try {
    const response = await axios.put(`${baseUrl}/HardwareProducts`, productData, {
      headers: jsonHeaders(apiAccessToken),
    });
    return response.data;
  } catch (error) {
    if (error?.response?.status === 409) {
      throw new Error("The product was changed by someone else. Reload the page and try again.");
    }
    throw failWith(error, "Failed to update hardware product");
  }
}

export async function deleteHardwareProduct(hardwareProductPublicId, apiAccessToken) {
  try {
    const response = await axios.delete(
      `${baseUrl}/HardwareProducts/${encodeURIComponent(hardwareProductPublicId)}`,
      { headers: { Authorization: `Bearer ${apiAccessToken}` } }
    );
    return response.data;
  } catch (error) {
    throw failWith(error, "Failed to delete hardware product");
  }
}

/** The assortment of one agreement: which products are visible agreement-wide or per billing group. */
export async function getAgreementHardwareAssortment(agreementPublicId, apiAccessToken) {
  try {
    const response = await axios.get(
      `${baseUrl}/HardwareProducts/assortment/admin/by-agreement/${encodeURIComponent(agreementPublicId)}`,
      { headers: jsonHeaders(apiAccessToken) }
    );
    return response.data;
  } catch (error) {
    throw failWith(error, "Failed to fetch hardware assortment");
  }
}

/** Replaces the whole assortment of the agreement. Last writer wins. */
export async function replaceAgreementHardwareAssortment(agreementPublicId, assortmentData, apiAccessToken) {
  try {
    const response = await axios.put(
      `${baseUrl}/HardwareProducts/assortment/admin/by-agreement/${encodeURIComponent(agreementPublicId)}`,
      assortmentData,
      { headers: jsonHeaders(apiAccessToken) }
    );
    return response.data;
  } catch (error) {
    throw failWith(error, "Failed to save hardware assortment");
  }
}

/**
 * Uploads or replaces the product image (PNG, JPEG or WebP, at most 2 MB). The server decides
 * the format from the bytes. Returns the updated product with its signed display URL.
 */
export async function uploadHardwareProductImage(hardwareProductPublicId, file, apiAccessToken) {
  const formData = new FormData();
  formData.append("file", file);
  try {
    const response = await axios.post(
      `${baseUrl}/HardwareProducts/${encodeURIComponent(hardwareProductPublicId)}/image`,
      formData,
      { headers: { Authorization: `Bearer ${apiAccessToken}` } }
    );
    return response.data;
  } catch (error) {
    throw failWith(error, "Failed to upload the product image");
  }
}

/** Removes the uploaded image; the pasted image URL, if any, shows again. */
export async function deleteHardwareProductImage(hardwareProductPublicId, apiAccessToken) {
  try {
    const response = await axios.delete(
      `${baseUrl}/HardwareProducts/${encodeURIComponent(hardwareProductPublicId)}/image`,
      { headers: { Authorization: `Bearer ${apiAccessToken}` } }
    );
    return response.data;
  } catch (error) {
    throw failWith(error, "Failed to remove the product image");
  }
}
